import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Marquee from 'react-fast-marquee';
import { ExternalLink } from 'lucide-react';
import { FormModal, MODAL_COLORS } from '@/components/modal';
import { ArticleViewerModal } from './ArticleViewerModal';
import { useBreakingNews } from '../hooks';
import type { BreakingNewsItem } from '../hooks';
import type { FeedPost } from '../types';

/** Returns true if the first alphabetic character is Hebrew. */
function isHebrew(text: string): boolean {
  const match = text.match(/[\p{L}]/u);
  return !!match && /[\u0590-\u05FF]/.test(match[0]);
}

/** Extract a title from RSS post content (first line / first sentence). */
function extractTitle(content: string): string {
  const firstLine = content.split('\n')[0].replace(/[*#_]/g, '').trim();
  if (firstLine.length > 120) return firstLine.slice(0, 117) + '...';
  return firstLine;
}

/** Format time as HH:MM */
function formatTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Format today's date as DD.MM.YYYY */
function formatDate(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** Convert a BreakingNewsItem to a minimal FeedPost for ArticleViewerModal. */
function toFeedPost(item: BreakingNewsItem): FeedPost {
  return {
    id: item.id,
    content: item.content,
    mediaUrl: null,
    mediaType: null,
    mediaMeta: null,
    bgGradient: null,
    isPinned: false,
    viewCount: 0,
    source: 'rss',
    sourceUrl: item.sourceUrl,
    sourceName: item.sourceName,
    sourceIconUrl: item.sourceIconUrl,
    createdAt: item.createdAt,
    updatedAt: item.createdAt,
    author: { id: '', firstName: null, lastName: null, avatarUrl: null, role: '' },
    reactionCounts: {} as FeedPost['reactionCounts'],
    userReactions: [],
    commentCount: 0,
  };
}

// ═══════════════════════════════════════════════════════════════
// Breaking News Modal — full list of all items
// ═══════════════════════════════════════════════════════════════
function BreakingNewsModal({
  isOpen,
  onClose,
  items,
  onItemClick,
}: {
  isOpen: boolean;
  onClose: () => void;
  items: BreakingNewsItem[];
  onItemClick: (item: BreakingNewsItem) => void;
}) {
  const { t } = useTranslation();

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('feed.allBreakingNews')}
      maxWidth="max-w-lg"
    >
      <div className="space-y-1 -mx-1">
        {items.map((item) => {
          const title = extractTitle(item.content);
          const hebrew = isHebrew(title);

          return (
            <button
              key={item.id}
              onClick={() => onItemClick(item)}
              className="flex items-start gap-3 p-3 rounded-xl transition-colors hover:bg-slate-50 cursor-pointer w-full text-start"
              style={{
                border: `1px solid ${MODAL_COLORS.border}`,
              }}
              dir={hebrew ? 'rtl' : 'ltr'}
            >
              {item.sourceIconUrl && (
                <img
                  src={item.sourceIconUrl}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800 leading-snug">
                  {title}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
                  {item.sourceName && <span>{item.sourceName}</span>}
                  <span>{formatTime(item.createdAt)}</span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-1" />
            </button>
          );
        })}
      </div>
    </FormModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// BreakingNewsTicker — two-row layout
// ═══════════════════════════════════════════════════════════════
export function BreakingNewsTicker() {
  const { t } = useTranslation();
  const { data: items } = useBreakingNews();
  const [modalOpen, setModalOpen] = useState(false);
  const [articlePost, setArticlePost] = useState<FeedPost | null>(null);

  if (!items || items.length === 0) return null;

  // Detect if the majority of items are Hebrew → set marquee direction
  const hebrewCount = items.filter((item) => isHebrew(extractTitle(item.content))).length;
  const contentIsHebrew = hebrewCount > items.length / 2;
  const marqueeDirection = contentIsHebrew ? 'right' : 'left';

  const openArticle = (item: BreakingNewsItem) => {
    setArticlePost(toFeedPost(item));
  };

  return (
    <>
      <div
        className="feed-post-flat mt-5 mb-2 md:mb-3"
        style={{
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderTop: '1px solid rgba(148, 163, 184, 0.25)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        {/* Top row: label button + date */}
        <div
          className="flex items-center justify-between px-4 sm:px-6 md:px-3 py-1.5"
          style={{
            borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer"
            style={{
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
            }}
          >
            {t('feed.allBreakingNews')}
          </button>

          <span className="text-xs text-neutral-500 font-medium tabular-nums" dir="ltr">
            {formatDate()}
          </span>
        </div>

        {/* Bottom row: scrolling marquee — force LTR so the CSS animation
             isn't double-flipped by the page's RTL direction */}
        <div className="py-1 overflow-hidden" dir="ltr" style={{ minHeight: 32 }}>
          <Marquee
            pauseOnHover
            speed={40}
            direction={marqueeDirection}
          >
            {items.map((item) => {
              const title = extractTitle(item.content);
              const hebrew = isHebrew(title);

              return (
                <button
                  key={item.id}
                  onClick={() => openArticle(item)}
                  className="flex items-center gap-1.5 mx-5 cursor-pointer hover:opacity-70 transition-opacity"
                  dir={hebrew ? 'rtl' : 'ltr'}
                >
                  {item.sourceIconUrl && (
                    <img
                      src={item.sourceIconUrl}
                      alt=""
                      className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                    />
                  )}
                  <span
                    className="text-xs font-bold text-neutral-500 whitespace-nowrap tabular-nums"
                  >
                    {formatTime(item.createdAt)}
                  </span>
                  <span
                    className="text-xs font-medium text-neutral-800 whitespace-nowrap"
                  >
                    {title}
                  </span>
                </button>
              );
            })}
          </Marquee>
        </div>
      </div>

      {/* All breaking news list modal */}
      <BreakingNewsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        items={items}
        onItemClick={(item) => {
          setModalOpen(false);
          openArticle(item);
        }}
      />

      {/* Article viewer modal (same as regular RSS post viewer) */}
      <ArticleViewerModal
        post={articlePost}
        isOpen={!!articlePost}
        onClose={() => setArticlePost(null)}
      />
    </>
  );
}
